import { useState, useCallback, useRef, useEffect } from 'react'
import { InterviewService } from '@/services/interviewService'
import { InterviewState, InterviewMessage, AIResponse, CompetencyArea } from '@/types/interview'
import { COMPETENCY_AREAS, DEFAULT_INTERVIEW_CONFIG } from '@/constants/competencies'
import { useAppSelector } from '@/hooks/redux'
import { toast } from 'sonner'
import { 
  validateAndCorrectCompetencyState, 
  detectInterviewEnd,
  logCompetencySync 
} from '@/utils/competencySync'
import { apiUrl } from '@/utils/api'

interface UseInterviewReturn {
  state: InterviewState
  isLoading: boolean
  error: string | null
  startInterview: () => void
  sendMessage: (message: string) => Promise<void>
  endInterview: (reason?: string) => void
  resetInterview: () => void
}

export const useInterview = (): UseInterviewReturn => {
  const { candidateId, candidateName, position, organizationId, duration, employmentType, psychometricResultUrl, companyProfileUrl, reportCallbackUrl, noiseConsentData } = useAppSelector(state => state.auth)
  const inputMode = localStorage.getItem('interviewInputType') as 'speech' | 'text' || 'text'
  
  // Convert duration from minutes to seconds, fallback to default if invalid
  const interviewTimeLimit = (() => {
    const durationMinutes = duration ? parseInt(duration) : null
    return durationMinutes && !isNaN(durationMinutes) && durationMinutes > 0 
      ? durationMinutes * 60 
      : DEFAULT_INTERVIEW_CONFIG.timeLimit
  })()
  
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const interviewServiceRef = useRef<InterviewService | null>(null)
  const lastCompetencyToastRef = useRef<string | null>(null)

  const [state, setState] = useState<InterviewState>(() => {
    const initialCompetencies = COMPETENCY_AREAS.map(area => ({ ...area }))
    
    return {
      isActive: false,
      isCompleted: false,
      currentCompetencyIndex: 0,
      currentCompetency: initialCompetencies[0],
      totalQuestions: 0,
      questionsAsked: 0,
      startTime: new Date(),
      duration: 0,
      messages: [],
      competencies: initialCompetencies,
      overallProgress: 0,
      candidateInfo: {
        id: candidateId || '',
        name: candidateName || '',
        position: position || '',
        organizationId: organizationId || ''
      },
      inputMode
    }
  })

  // Timer effect - countdown from time limit
  useEffect(() => {
    if (state.isActive && !state.isCompleted) {
      timerRef.current = setInterval(() => {
        setState(prev => {
          const elapsed = Math.floor((Date.now() - prev.startTime.getTime()) / 1000)
          const remaining = Math.max(0, interviewTimeLimit - elapsed)

          // Auto-complete interview when time runs out
          if (remaining === 0 && !prev.isCompleted) {
            // Trigger side effects outside of setState
            setTimeout(async () => {
              console.log('Interview time limit reached, auto-completing...')
              
              // Call backend to end the session and update status
              try {
                const sessionId = localStorage.getItem('interview-session-id')
                if (sessionId) {
                  const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/end`), {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      ended_by_timer: true,
                      end_reason: 'Time limit reached'
                    })
                  })
                  
                  if (!response.ok) {
                    console.error('Failed to end interview session in backend (timer)')
                  } else {
                    console.log('Interview session ended successfully in backend (timer)')
                  }
                }
              } catch (error) {
                console.error('Error ending interview session (timer):', error)
              }
              
              // Show completion toast
              toast.success('Interview completed! Time limit reached.')
            }, 0)
            
            return {
              ...prev,
              isActive: false,
              isCompleted: true,
              overallProgress: 100,
              duration: interviewTimeLimit
            }
          }

          return {
            ...prev,
            duration: remaining
          }
        })
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [state.isActive, state.isCompleted, interviewTimeLimit])

  const addMessage = useCallback((message: Omit<InterviewMessage, 'id' | 'timestamp'>) => {
    const newMessage: InterviewMessage = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date()
    }

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }))

    return newMessage
  }, [])

  // Removed separate updateCompetencyProgress and transitionToNextCompetency functions
  // State updates are now handled directly in sendMessage for better synchronization

  const startInterview = useCallback(async () => {
    if (!candidateId || !candidateName || !position) {
      setError('Missing candidate information')
      return
    }

    try {
      // Get existing session ID (should be created by Welcome page)
      const sessionId = localStorage.getItem('interview-session-id')
      if (!sessionId) {
        console.error('No interview session ID found - session should be created before starting interview')
        throw new Error('Interview session not found. Please start from the welcome page.')
      }

      // Initialize interview service
      interviewServiceRef.current = new InterviewService({
        candidateId,
        candidateName,
        position,
        organizationId: organizationId || '',
        inputMode,
        employmentType: employmentType || 'Full-time',
        duration: duration || '30',
        psychometricResultUrl: psychometricResultUrl || undefined,
        companyProfileUrl: companyProfileUrl || undefined,
        reportCallbackUrl: reportCallbackUrl || undefined
      })

      // Create session in backend immediately when interview starts
      try {
        await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/process-response`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            response: "INTERVIEW_START", // Special marker to indicate interview start
            candidate_info: {
              candidate_id: candidateId,
              candidate_name: candidateName,
              position: position,
              organization_id: organizationId || '',
              employment_type: employmentType || 'Full-time',
              interview_duration: duration ? parseInt(duration) * 60 : 1800,
              psychometric_result_url: psychometricResultUrl,
              company_profile_url: companyProfileUrl,
              report_callback_url: reportCallbackUrl,
              // Include noise consent data
              noise_consent_given: noiseConsentData?.consentGiven || false,
              noise_level_detected: noiseConsentData?.noiseLevel || null,
              environment_recommendation: noiseConsentData?.recommendation || null
            }
          })
        })
        console.log('Session created in backend at interview start')
      } catch (error) {
        console.warn('Failed to create session at start, will create on first message:', error)
      }

      // Generate initial question
      const initialQuestion = interviewServiceRef.current.generateInitialQuestion(state.currentCompetency)
      
      // Add initial AI message
      addMessage({
        role: 'assistant',
        content: initialQuestion,
        competency: state.currentCompetency.name,
        questionNumber: 1
      })

      setState(prev => ({
        ...prev,
        isActive: true,
        startTime: new Date(),
        messages: [{
          id: Date.now().toString(),
          role: 'assistant',
          content: initialQuestion,
          timestamp: new Date(),
          competency: prev.currentCompetency.name,
          questionNumber: 1
        }]
      }))

      // toast.success('Interview started!')
    } catch (err) {
      setError('Failed to start interview')
      console.error('Error starting interview:', err)
    }
  }, [candidateId, candidateName, position, organizationId, inputMode, employmentType, duration, psychometricResultUrl, companyProfileUrl, reportCallbackUrl, noiseConsentData, state.currentCompetency, addMessage])

  const sendMessage = useCallback(async (message: string) => {
    if (!state.isActive || !interviewServiceRef.current) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      // Add user message
      addMessage({
        role: 'user',
        content: message,
        competency: state.currentCompetency.name
      })

      // Get AI response
      const timeRemaining = interviewTimeLimit ? 
        interviewTimeLimit - state.duration : undefined

      // Get existing session ID (should be created by Welcome page)
      const sessionId = localStorage.getItem('interview-session-id')
      if (!sessionId) {
        console.error('No interview session ID found during message send')
        throw new Error('Interview session not found. Please start from the welcome page.')
      }

      let aiResponse: AIResponse = await interviewServiceRef.current.sendMessage(
        message,
        state.messages,
        state.currentCompetency,
        state.competencies,
        state.currentCompetency.questionsAsked,
        timeRemaining,
        sessionId
      )

      // ENHANCED JSON RESPONSE PROCESSING: Handle cases where AI returns JSON instead of clean text
      let cleanedResponse = aiResponse.next_response
      
      if (typeof aiResponse.next_response === 'string') {
        const originalResponse = aiResponse.next_response
        
        // Method 1: Check if the entire response is a JSON object
        if (originalResponse.trim().startsWith('{') && originalResponse.trim().endsWith('}')) {
          try {
            const directJson = JSON.parse(originalResponse.trim())
            console.log('🔍 DIRECT JSON RESPONSE DETECTED:', directJson)
            
            // Override AI response fields with parsed JSON values
            if (directJson.end_interview !== undefined) {
              aiResponse.end_interview = directJson.end_interview
            }
            if (directJson.current_competency) {
              aiResponse.current_competency = directJson.current_competency
            }
            if (directJson.competency_action) {
              aiResponse.competency_action = directJson.competency_action
            }
            if (typeof directJson.question_count === 'number') {
              aiResponse.question_count = directJson.question_count
            }
            
            // Use the clean text content for display
            cleanedResponse = directJson.next_response || directJson.content || directJson.message || ''
            console.log('🔄 EXTRACTED CLEAN CONTENT:', cleanedResponse)
          } catch (e) {
            console.warn('⚠️ Failed to parse direct JSON response:', e)
            // Fallback: extract any readable text from the malformed JSON
            const textMatch = originalResponse.match(/"(?:next_response|content|message)"\s*:\s*"([^"]+)"/)
            if (textMatch) {
              cleanedResponse = textMatch[1]
            } else {
              cleanedResponse = 'Thank you for your response. Let me continue with the next question.'
            }
          }
        }
        // Method 2: Look for JSON blocks within text content
        else {
          const jsonMatches = originalResponse.match(/\{[^{}]*"(?:end_interview|next_response|current_competency)"[^{}]*\}/g)
          if (jsonMatches && jsonMatches.length > 0) {
            try {
              const parsedJson = JSON.parse(jsonMatches[0])
              console.log('🔍 EMBEDDED JSON IN RESPONSE:', parsedJson)
              
              // Override relevant fields
              if (parsedJson.end_interview !== undefined) {
                aiResponse.end_interview = parsedJson.end_interview
              }
              if (parsedJson.current_competency) {
                aiResponse.current_competency = parsedJson.current_competency
              }
              if (parsedJson.competency_action) {
                aiResponse.competency_action = parsedJson.competency_action
              }
              if (typeof parsedJson.question_count === 'number') {
                aiResponse.question_count = parsedJson.question_count
              }
              
              // Clean the response text by removing JSON blocks
              cleanedResponse = originalResponse.replace(/\{[^{}]*"(?:end_interview|next_response|current_competency)"[^{}]*\}/g, '').trim()
              
              // If we have clean text from JSON, use that instead
              if (parsedJson.next_response && typeof parsedJson.next_response === 'string') {
                cleanedResponse = parsedJson.next_response
              }
            } catch (e) {
              console.warn('⚠️ Failed to parse embedded JSON:', e)
              // Remove malformed JSON blocks
              cleanedResponse = originalResponse.replace(/\{[^{}]*"[^{}]*\}/g, '').trim()
            }
          }
        }
        
        // Final cleanup: ensure we have meaningful content
        if (!cleanedResponse || cleanedResponse.match(/^[\s\{\}\[\]",:;]*$/)) {
          // Provide context-appropriate fallback based on interview state
          if (aiResponse.end_interview === 'yes') {
            cleanedResponse = 'Thank you for your participation in this interview.'
          } else {
            cleanedResponse = 'Thank you for your response. Let me continue with the next question.'
          }
          console.log('📝 PROVIDED FALLBACK RESPONSE:', cleanedResponse)
        }
        
        // Additional cleaning for any remaining artifacts
        cleanedResponse = cleanedResponse
          .replace(/"\s*,\s*$/g, '') // Remove trailing quotes and commas
          .replace(/^\s*"|"\s*$/g, '') // Remove leading/trailing quotes
          .replace(/\\n/g, ' ') // Replace escaped newlines
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim()
      }

      // Add AI response message
      addMessage({
        role: 'assistant',
        content: cleanedResponse,
        competency: aiResponse.current_competency,
        questionNumber: aiResponse.question_count
      })

      // Sync competency state with AI response using enhanced utilities
      setState(prev => {
        const aiCompetencyName = aiResponse.current_competency
        
        // Use enhanced competency synchronization
        const currentState = {
          competencyIndex: prev.currentCompetencyIndex,
          competencyName: prev.currentCompetency.name,
          questionsAsked: prev.currentCompetency.questionsAsked
        }
        
        const validationResult = validateAndCorrectCompetencyState(
          aiResponse,
          currentState,
          prev.competencies
        )
        
        console.log(`🔍 COMPETENCY SYNC: AI reported "${aiCompetencyName}", Frontend is on "${prev.currentCompetency.name}" (index: ${prev.currentCompetencyIndex})`)
        console.log(`🔧 VALIDATION RESULT:`, validationResult)
        
        // SPECIAL DEBUGGING for Learning Ability and final competencies
        if (prev.currentCompetency.name === "Learning Ability" || aiCompetencyName === "Learning Ability") {
          console.warn("🚨 LEARNING ABILITY SYNC DETECTED!")
          console.warn(`  Frontend: "${prev.currentCompetency.name}" (index ${prev.currentCompetencyIndex})`)
          console.warn(`  AI wants: "${aiCompetencyName}"`)
          console.warn(`  Expected next after Learning Ability: "Skill Match" (index 9)`)
        }
        
        const finalCompetencies = ["Skill Match", "Work History Strength", "Leadership"]
        if (finalCompetencies.includes(prev.currentCompetency.name) || finalCompetencies.includes(aiCompetencyName)) {
          console.warn("🏁 FINAL COMPETENCY SYNC:")
          console.warn(`  Frontend: "${prev.currentCompetency.name}" is final: ${finalCompetencies.includes(prev.currentCompetency.name)}`)
          console.warn(`  AI wants: "${aiCompetencyName}" is final: ${finalCompetencies.includes(aiCompetencyName)}`)
        }
        
        // Create standardized competency mapping
        const competencyMappings = {
          // Frontend competency name -> index mapping
          frontend: new Map(prev.competencies.map((comp, idx) => [comp.name, idx])),
          
          // Standardized mapping (frontend and backend MUST match exactly)
          standardized: new Map([
            ['Cultural Fit', 0],
            ['Technical Skills', 1], 
            ['Problem Solving', 2],
            ['Communication', 3],
            ['Relevance to Job Role', 4],
            ['Success Prediction Score', 5],
            ['Turnover Risk', 6],
            ['Proactivity Score', 7],
            ['Learning Ability', 8],
            ['Skill Match', 9],
            ['Work History Strength', 10],
            ['Leadership', 11]
          ])
        }
        
        let aiCompetencyIndex = -1
        
        // Strategy 1: Direct exact match with frontend competencies
        if (competencyMappings.frontend.has(aiCompetencyName)) {
          aiCompetencyIndex = competencyMappings.frontend.get(aiCompetencyName)!
          console.log(`✅ EXACT MATCH: "${aiCompetencyName}" -> index ${aiCompetencyIndex}`)
        }
        // Strategy 2: Standardized mapping
        else if (competencyMappings.standardized.has(aiCompetencyName)) {
          aiCompetencyIndex = competencyMappings.standardized.get(aiCompetencyName)!
          console.log(`🔄 BACKEND MAPPING: "${aiCompetencyName}" -> index ${aiCompetencyIndex} ("${prev.competencies[aiCompetencyIndex].name}")`)
        }
        // Strategy 3: Fuzzy matching for partial names
        else {
          const normalizeText = (text: string) => text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim()
          const normalizedAI = normalizeText(aiCompetencyName)
          
          aiCompetencyIndex = prev.competencies.findIndex(comp => {
            const normalizedName = normalizeText(comp.name)
            const normalizedDisplay = normalizeText(comp.displayName)
            
            // Exact normalized match
            if (normalizedName === normalizedAI || normalizedDisplay === normalizedAI) return true
            
            // Key word matching for complex names
            const aiWords = normalizedAI.split(' ').filter(w => w.length > 2)
            const nameWords = normalizedName.split(' ').filter(w => w.length > 2)
            
            if (aiWords.length > 0 && nameWords.length > 0) {
              return aiWords.some(aiWord => nameWords.some(nameWord => 
                aiWord.includes(nameWord) || nameWord.includes(aiWord)
              ))
            }
            
            return false
          })
          
          if (aiCompetencyIndex !== -1) {
            console.log(`🔍 FUZZY MATCH: "${aiCompetencyName}" -> index ${aiCompetencyIndex} ("${prev.competencies[aiCompetencyIndex].name}")`)
          }
        }

        // CRITICAL: Final validation and fallback
        if (aiCompetencyIndex === -1) {
          console.error(`❌ COMPETENCY SYNC FAILURE: No match found for AI competency: "${aiCompetencyName}"`)
          console.error(`Available frontend competencies:`, prev.competencies.map((c, idx) => `[${idx}] "${c.name}"`))
          
          // STRICT SEQUENTIAL ENFORCEMENT: Only allow immediate next competency
          if (aiResponse.competency_action === 'transition') {
            const nextIndex = prev.currentCompetencyIndex + 1
            if (nextIndex < prev.competencies.length) {
              // Move to immediate next competency only
              aiCompetencyIndex = nextIndex
              console.warn(`🔒 ENFORCED SEQUENTIAL PROGRESSION: "${prev.currentCompetency.name}" -> "${prev.competencies[nextIndex].name}"`)
            } else {
              // At end of sequence - stay on current
              aiCompetencyIndex = prev.currentCompetencyIndex
              console.warn(`🏁 END OF SEQUENCE: Staying on "${prev.currentCompetency.name}"`)
            }
          } else {
            // Stay on current competency
            aiCompetencyIndex = prev.currentCompetencyIndex
            console.warn(`⏹️ STAYING PUT: Remaining on current competency [${aiCompetencyIndex}] "${prev.currentCompetency.name}"`)
          }
        }
        
        // VALIDATION: Ensure aiCompetencyIndex is within bounds
        aiCompetencyIndex = Math.max(0, Math.min(aiCompetencyIndex, prev.competencies.length - 1))

        // Use validated competency index
        let finalCompetencyIndex = validationResult.wasCorreted ? 
          validationResult.correctedIndex : aiCompetencyIndex
        
        // Ensure we stay within bounds
        finalCompetencyIndex = Math.max(0, Math.min(finalCompetencyIndex, prev.competencies.length - 1))
        
        // Log detailed synchronization information
        logCompetencySync(
          aiCompetencyName,
          {
            frontendIndex: finalCompetencyIndex,
            frontendName: prev.competencies[finalCompetencyIndex].name,
            backendName: aiCompetencyName,
            isValidMapping: !validationResult.wasCorreted
          },
          {
            fromIndex: prev.currentCompetencyIndex,
            toIndex: finalCompetencyIndex,
            action: aiResponse.competency_action || 'stay'
          },
          {
            old: prev.currentCompetency.questionsAsked,
            new: validationResult.correctedQuestions
          },
          {
            'AI Response': aiResponse,
            'Corrections Applied': validationResult.corrections,
            'Was Corrected': validationResult.wasCorreted
          }
        )
        
        aiCompetencyIndex = finalCompetencyIndex

        let newState = { ...prev }
        let newCompetencies = [...prev.competencies]

        // ENHANCED COMPETENCY TRANSITION LOGIC using validated results
        const isTransitioning = finalCompetencyIndex !== prev.currentCompetencyIndex
        
        if (isTransitioning) {
          console.log(`🔄 COMPETENCY TRANSITION: [${prev.currentCompetencyIndex}] "${prev.currentCompetency.name}" -> [${finalCompetencyIndex}] "${prev.competencies[finalCompetencyIndex].name}"`)
          
          // The validation has already been done in validateAndCorrectCompetencyState
          // Use the corrected index for safe transitions
          if (finalCompetencyIndex > prev.currentCompetencyIndex) {
            // Mark the CURRENT competency as completed (not the target one)
            if (!newCompetencies[prev.currentCompetencyIndex].completed) {
              newCompetencies[prev.currentCompetencyIndex] = { 
                ...newCompetencies[prev.currentCompetencyIndex], 
                completed: true 
              }
              console.log(`✅ Marked [${prev.currentCompetencyIndex}] "${newCompetencies[prev.currentCompetencyIndex].name}" as completed`)
            }
          }
          
          // Update to new competency
          newState.currentCompetencyIndex = finalCompetencyIndex
          newState.currentCompetency = newCompetencies[finalCompetencyIndex]
          
          // Show transition notification only for actual forward progression
          if (finalCompetencyIndex > prev.currentCompetencyIndex) {
            const toastKey = `${prev.currentCompetency.name}->${newState.currentCompetency.name}`
            if (lastCompetencyToastRef.current !== toastKey) {
              lastCompetencyToastRef.current = toastKey
              // Use a more visually prominent message (all caps + icon) while
              // keeping this file as plain TypeScript (no JSX).
              toast.info(
                `📍 NOW ASSESSING: ${newState.currentCompetency.displayName.toUpperCase()}`
              )
            }
          }
          
          console.log(`✅ Transition completed to [${finalCompetencyIndex}] "${newState.currentCompetency.name}"`)
        } else {
          console.log(`📍 Staying on competency [${finalCompetencyIndex}] "${prev.competencies[finalCompetencyIndex].name}"`)
        }

        // QUESTION COUNT MANAGEMENT with validated results
        const targetCompetencyIndex = newState.currentCompetencyIndex
        const oldQuestionCount = newCompetencies[targetCompetencyIndex].questionsAsked
        
        // Use the corrected question count from validation
        const newQuestionCount = validationResult.correctedQuestions
        
        if (isTransitioning && finalCompetencyIndex > prev.currentCompetencyIndex) {
          console.log(`🆕 NEW COMPETENCY: Question count set to ${newQuestionCount} for "${newState.currentCompetency.name}"`)
        } else {
          console.log(`➕ SAME COMPETENCY: Question count updated to ${newQuestionCount} for "${newState.currentCompetency.name}"`)
        }
        
        // Update the competency with validated question count
        newCompetencies[targetCompetencyIndex] = {
          ...newCompetencies[targetCompetencyIndex],
          questionsAsked: newQuestionCount
        }
        
        console.log(`✅ Updated question count: "${newState.currentCompetency.name}" ${oldQuestionCount} -> ${newQuestionCount} (AI reported: ${aiResponse.question_count}, validated: ${validationResult.wasCorreted ? 'YES' : 'NO'})`)
        
        // FINAL VALIDATION for state consistency
        const expectedName = newCompetencies[newState.currentCompetencyIndex].name
        if (newState.currentCompetency.name !== expectedName) {
          console.error(`🚨 STATE INCONSISTENCY DETECTED!`)
          console.error(`  State competency: "${newState.currentCompetency.name}"`)
          console.error(`  Expected at index ${newState.currentCompetencyIndex}: "${expectedName}"`)
          
          // Force correction
          newState.currentCompetency = newCompetencies[newState.currentCompetencyIndex]
          console.warn(`🔧 FORCED STATE CORRECTION: Now on "${newState.currentCompetency.name}"`)
        }
        
        // Log any corrections that were applied
        if (validationResult.wasCorreted && validationResult.corrections.length > 0) {
          console.warn(`🔧 SYNC CORRECTIONS APPLIED:`, validationResult.corrections)
        }
        
        // Validate all competencies have proper state
        console.log(`📊 COMPETENCY STATE VALIDATION:`)
        newCompetencies.forEach((comp, idx) => {
          const isCurrent = idx === targetCompetencyIndex
          const isCompleted = comp.completed
          const questionCount = comp.questionsAsked
          
          console.log(`  [${idx}] ${comp.name}: ${questionCount} questions, completed: ${isCompleted}, current: ${isCurrent}`)
          
          // Fix any remaining corrupted state that validation might have missed
          if (questionCount < 0) {
            console.warn(`🔧 FINAL FIX: Negative question count for ${comp.name}`)
            newCompetencies[idx] = { ...comp, questionsAsked: 1 }
          }
          if (questionCount > comp.maxQuestions + 3) { // Allow more flexibility
            console.warn(`🔧 FINAL FIX: Excessive question count (${questionCount}) for ${comp.name}`)
            newCompetencies[idx] = { ...comp, questionsAsked: Math.min(questionCount, comp.maxQuestions + 2) }
          }
        })

        // Calculate overall progress with more granular tracking
        const completedCount = newCompetencies.filter(comp => comp.completed).length
        const totalCompetencies = newCompetencies.length
        
        // Add partial progress for current competency based on questions asked
        let currentCompetencyProgress = 0
        if (newState.currentCompetencyIndex < totalCompetencies) {
          const currentComp = newCompetencies[newState.currentCompetencyIndex]
          const progressInCurrentCompetency = Math.min(currentComp.questionsAsked / currentComp.maxQuestions, 1.0)
          currentCompetencyProgress = progressInCurrentCompetency / totalCompetencies
        }
        
        newState.overallProgress = Math.min(100, (completedCount / totalCompetencies) * 100 + (currentCompetencyProgress * 100))
        newState.competencies = newCompetencies
        
        console.log(`📈 Progress calculation:`)
        console.log(`   - Completed competencies: ${completedCount}/${totalCompetencies}`)
        console.log(`   - Current competency progress: ${newCompetencies[newState.currentCompetencyIndex]?.questionsAsked || 0}/${newCompetencies[newState.currentCompetencyIndex]?.maxQuestions || 5}`)
        console.log(`   - Overall progress: ${newState.overallProgress.toFixed(1)}%`)

        console.log(`📋 ALL ${newCompetencies.length} COMPETENCIES STATUS:`)
        newCompetencies.forEach((comp, index) => {
          const status = comp.completed ? '✅' : (index === newState.currentCompetencyIndex ? '🔄' : '⏳')
          const correctionFlag = validationResult.wasCorreted && index === newState.currentCompetencyIndex ? ' (CORRECTED)' : ''
          console.log(`   ${status} [${index}] ${comp.name}: ${comp.questionsAsked}/${comp.maxQuestions} questions${correctionFlag}`)
        })

        return newState
      })

      // ENHANCED INTERVIEW END DETECTION using improved utility function
      const endDetection = detectInterviewEnd(
        aiResponse,
        state.currentCompetencyIndex,
        state.competencies.length
      )

      console.log('🔚 INTERVIEW END CHECK:', {
        aiResponse_end_interview: aiResponse.end_interview,
        aiResponse_next_response: aiResponse.next_response?.substring(0, 100) + '...',
        endDetection,
        currentCompetencyIndex: state.currentCompetencyIndex,
        currentCompetency: state.currentCompetency.name
      })

      if (endDetection.shouldEnd) {
        console.log(`🎉 ENDING INTERVIEW: ${endDetection.reason}`)
        setState(prev => ({
          ...prev,
          isActive: false,
          isCompleted: true,
          overallProgress: 100
        }))
        
        toast.success('Interview completed!')
      }

    } catch (err) {
      setError('Failed to send message')
      console.error('Error sending message:', err)
      toast.error('Failed to get AI response. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [state, addMessage])

  const endInterview = useCallback(async (reason: string = 'Manual completion by candidate - unspecified trigger') => {
    try {
      // Call backend to end the session and update status
      const sessionId = localStorage.getItem('interview-session-id')
      if (sessionId) {
        const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/end`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ended_by_timer: false,
            end_reason: reason
          })
        })
        
        if (!response.ok) {
          console.error('Failed to end interview session in backend')
        } else {
          console.log(`Interview session ended successfully in backend. Reason: ${reason}`)
        }
      }
    } catch (error) {
      console.error('Error ending interview session:', error)
    }

    setState(prev => ({
      ...prev,
      isActive: false,
      isCompleted: true
    }))

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    toast.info('Interview ended')
  }, [])

  const resetInterview = useCallback(() => {
    const initialCompetencies = COMPETENCY_AREAS.map(area => ({ ...area }))
    
    setState({
      isActive: false,
      isCompleted: false,
      currentCompetencyIndex: 0,
      currentCompetency: initialCompetencies[0],
      totalQuestions: 0,
      questionsAsked: 0,
      startTime: new Date(),
      duration: 0,
      messages: [],
      competencies: initialCompetencies,
      overallProgress: 0,
      candidateInfo: {
        id: candidateId || '',
        name: candidateName || '',
        position: position || '',
        organizationId: organizationId || ''
      },
      inputMode
    })

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    setError(null)
    interviewServiceRef.current = null
  }, [candidateId, candidateName, position, organizationId, inputMode, employmentType, duration, psychometricResultUrl, companyProfileUrl, reportCallbackUrl, noiseConsentData])

  return {
    state,
    isLoading,
    error,
    startInterview,
    sendMessage,
    endInterview,
    resetInterview
  }
}